/**
 * login.tsx — Pantalla de login (réplica del original puntodeventa.online,
 * Account/Login.cshtml): fondo claro, logo Kapi arriba, card blanca centrada,
 * título "Iniciar sesión", campos con floating-label (foco azul), ojo para
 * mostrar/ocultar la contraseña, link "Restablecer contraseña", botón teal
 * "Iniciar sesión" y la línea "¿Aún no tienes una cuenta? Regístrate gratis aquí".
 *
 * El backend es de un solo paso (POST /api/auth/login, ASP.NET Identity → JWT);
 * el flujo multi-sucursal se resuelve después con el SucursalSelectorModal.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { esDestinoSistema, useAuth } from '../lib/auth';
import LoadingScreen from '../components/LoadingScreen';

type Mode = 'login' | 'forgot' | 'reset';

export default function LoginPage(): React.ReactElement {
  const { login, loading, error, user, destination } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // N-PL16 / N2-PL02: prod ignora returnUrl y entra al destino de RedirectToModule; returnTo solo queda para una sesión
  // restaurada o mientras falte elegir sucursal (el selector lleva al destino al elegir).
  const returnTo = destination ?? searchParams.get('returnTo') ?? '/';

  const [mode, setMode] = useState<Mode>('login');
  const [form, setForm] = useState({ username: '', password: '' });
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetForm, setResetForm] = useState({ email: '', code: '', newPassword: '', confirm: '' });
  // Registro de prospecto (paridad /registro del .NET: Name, Email, BranchName, Phone, CallMe)
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (user && !loading) {
      // Un documento /sistema no es ruta de la SPA vieja: recarga completa (plan §1.6).
      if (esDestinoSistema(returnTo)) window.location.assign(returnTo);
      else navigate(returnTo, { replace: true });
    }
  }, [user, loading, navigate, returnTo]);

  if (loading || user) {
    return <LoadingScreen />;
  }

  function switchMode(m: Mode): void {
    setMode(m);
    setLocalError(null);
    setInfo(null);
  }

  async function handleForgotPassword(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLocalError(null);
    if (!forgotEmail.trim()) { setLocalError('Ingresa tu correo'); return; }
    setSubmitting(true);
    try {
      const r = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      const data: { isSuccess: boolean; message?: string } = await r.json();
      if (data.isSuccess) {
        // El código viaja POR CORREO (paridad prod); aquí solo se precarga el email.
        setResetForm((p) => ({ ...p, email: forgotEmail.trim() }));
        setInfo(data.message ?? 'Por favor revise su correo electrónico para restablecer su contraseña');
        switchMode('reset');
      } else {
        setLocalError(data.message ?? 'Error al solicitar el código');
      }
    } catch {
      setLocalError('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResetPassword(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLocalError(null);
    if (!resetForm.email || !resetForm.code || !resetForm.newPassword) {
      setLocalError('Todos los campos son requeridos'); return;
    }
    if (resetForm.newPassword !== resetForm.confirm) {
      setLocalError('Las contraseñas no coinciden'); return;
    }
    if (resetForm.newPassword.length < 6) {
      setLocalError('La contraseña debe tener al menos 6 caracteres'); return;
    }
    setSubmitting(true);
    try {
      const r = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetForm.email, code: resetForm.code, newPassword: resetForm.newPassword, confirmPassword: resetForm.confirm }),
      });
      const data: { isSuccess: boolean; message?: string } = await r.json();
      if (data.isSuccess) {
        setInfo('Contraseña restablecida con éxito. Inicia sesión con tu nueva contraseña.');
        setForm((p) => ({ ...p, username: resetForm.email }));
        switchMode('login');
      } else {
        setLocalError(data.message ?? 'Código inválido');
      }
    } catch {
      setLocalError('Error de conexión');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setLocalError(null);
    setInfo(null);

    if (!form.username.trim()) {
      setLocalError('Ingresa tu correo electrónico');
      return;
    }
    if (!form.password) {
      setLocalError('Ingresa tu contraseña');
      return;
    }

    setSubmitting(true);
    try {
      // Con éxito, el efecto de arriba navega al destino que trae el login.
      await login({ username: form.username.trim(), password: form.password, rememberMe: true });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error al iniciar sesión';
      setLocalError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const displayError = localError ?? error;
  const isLoading = loading || submitting;

  const title =
    mode === 'forgot' ? 'Restablecer contraseña'
    : mode === 'reset' ? 'Nueva contraseña'
    : 'Iniciar sesión';

  return (
    <div className="min-h-dvh bg-slate-50 flex flex-col items-center px-4 py-10">
      {/* Logo */}
      <div className="mb-6 mt-6">
        <img src="/kapi.png" alt="Kapi" className="h-14 w-auto" />
      </div>

      {/* Card */}
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-sm px-7 py-8">
        <h1 className="text-center text-xl font-medium text-slate-500 mb-6">{title}</h1>

        {displayError && (
          <div
            role="alert"
            className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700 flex items-center gap-2"
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 9v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
            {displayError}
          </div>
        )}
        {info && (
          <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-700">
            {info}
          </div>
        )}

        <form onSubmit={(e) => { void handleSubmit(e); }} noValidate className={`space-y-4 ${mode !== 'login' ? 'hidden' : ''}`}>
          {/* Correo — floating label */}
          <div className="relative">
            <input
              id="login-email"
              type="email"
              autoComplete="username"
              autoFocus
              placeholder=" "
              className="peer w-full rounded-lg border border-slate-300 px-3 pt-5 pb-2 text-slate-800 placeholder-transparent outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={form.username}
              onChange={(e) => setForm((p) => ({ ...p, username: e.target.value }))}
              disabled={isLoading}
            />
            <label
              htmlFor="login-email"
              className="pointer-events-none absolute left-3 top-3.5 text-slate-400 transition-all peer-focus:top-1.5 peer-focus:text-xs peer-focus:font-semibold peer-focus:text-blue-600 peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-slate-500"
            >
              Correo electrónico
            </label>
          </div>

          {/* Contraseña — floating label + ojo */}
          <div className="relative">
            <input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder=" "
              className="peer w-full rounded-lg border border-slate-300 px-3 pt-5 pb-2 pr-11 text-slate-800 placeholder-transparent outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
              disabled={isLoading}
            />
            <label
              htmlFor="login-password"
              className="pointer-events-none absolute left-3 top-3.5 text-slate-400 transition-all peer-focus:top-1.5 peer-focus:text-xs peer-focus:font-semibold peer-focus:text-blue-600 peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-slate-500"
            >
              Contraseña
            </label>
            <button
              type="button"
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.9 4.2A9.1 9.1 0 0 1 12 4c7 0 10 8 10 8a18 18 0 0 1-2.2 3.2M6.6 6.6A18 18 0 0 0 2 12s3 8 10 8a9 9 0 0 0 5.4-1.6M1 1l22 22M9.9 9.9a3 3 0 0 0 4.2 4.2" /></svg>
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3-8 10-8 10 8 10 8-3 8-10 8-10-8-10-8z" /><circle cx="12" cy="12" r="3" /></svg>
              )}
            </button>
          </div>

          {/* Restablecer contraseña */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => switchMode('forgot')}
              className="text-sm text-blue-600 hover:underline"
            >
              Restablecer contraseña
            </button>
          </div>

          {/* Botón */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold py-3 transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <span className="h-4 w-4 rounded-full border-2 border-white/30 border-t-white animate-spin" aria-hidden="true" />
                Verificando usuario..
              </>
            ) : (
              'Iniciar sesión'
            )}
          </button>
        </form>

        {/* Formulario: solicitar código de reset */}
        {mode === 'forgot' && (
          <form onSubmit={(e) => { void handleForgotPassword(e); }} noValidate className="space-y-4 mt-2">
            <div className="relative">
              <input
                id="forgot-email"
                type="email"
                autoFocus
                placeholder=" "
                className="peer w-full rounded-lg border border-slate-300 px-3 pt-5 pb-2 text-slate-800 placeholder-transparent outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={submitting}
              />
              <label htmlFor="forgot-email" className="pointer-events-none absolute left-3 top-3.5 text-slate-400 transition-all peer-focus:top-1.5 peer-focus:text-xs peer-focus:font-semibold peer-focus:text-blue-600 peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-slate-500">
                Correo electrónico
              </label>
            </div>
            <button type="submit" disabled={submitting} className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold py-3 transition-colors">
              {submitting ? 'Enviando…' : 'Solicitar código'}
            </button>
            <button type="button" onClick={() => switchMode('login')} className="w-full text-sm text-slate-500 hover:text-slate-700 underline">
              Volver al inicio de sesión
            </button>
          </form>
        )}

        {/* Formulario: ingresar código + nueva contraseña */}
        {mode === 'reset' && (
          <form onSubmit={(e) => { void handleResetPassword(e); }} noValidate className="space-y-4 mt-2">
            {[
              { id: 'reset-email', label: 'Correo electrónico', key: 'email' as const, type: 'email' },
              { id: 'reset-code', label: 'Código de verificación', key: 'code' as const, type: 'text' },
              { id: 'reset-pwd', label: 'Nueva contraseña', key: 'newPassword' as const, type: 'password' },
              { id: 'reset-confirm', label: 'Confirmar contraseña', key: 'confirm' as const, type: 'password' },
            ].map(({ id, label, key, type }) => (
              <div key={id} className="relative">
                <input
                  id={id}
                  type={type}
                  placeholder=" "
                  className="peer w-full rounded-lg border border-slate-300 px-3 pt-5 pb-2 text-slate-800 placeholder-transparent outline-none transition-colors focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  value={resetForm[key]}
                  onChange={(e) => setResetForm((p) => ({ ...p, [key]: e.target.value }))}
                  disabled={submitting}
                />
                <label htmlFor={id} className="pointer-events-none absolute left-3 top-3.5 text-slate-400 transition-all peer-focus:top-1.5 peer-focus:text-xs peer-focus:font-semibold peer-focus:text-blue-600 peer-[:not(:placeholder-shown)]:top-1.5 peer-[:not(:placeholder-shown)]:text-xs peer-[:not(:placeholder-shown)]:font-semibold peer-[:not(:placeholder-shown)]:text-slate-500">
                  {label}
                </label>
              </div>
            ))}
            <button type="submit" disabled={submitting} className="w-full rounded-lg bg-emerald-500 hover:bg-emerald-600 disabled:opacity-60 text-white font-semibold py-3 transition-colors">
              {submitting ? 'Guardando…' : 'Restablecer contraseña'}
            </button>
            <button type="button" onClick={() => switchMode('login')} className="w-full text-sm text-slate-500 hover:text-slate-700 underline">
              Volver al inicio de sesión
            </button>
          </form>
        )}


      </div>
    </div>
  );
}
